import React from "react";
import {
  Target,
  Eye,
  Award,
  Users,
  Globe,
  TrendingUp,
  Shield,
  Clock,
  Lightbulb,
  Leaf,
  Building2,
  Truck,
  MessageCircle,
} from "lucide-react";
import Card from "../modern-components/ui/Card";

const About = () => {
  const values = [
    {
      icon: Shield,
      title: "Integrity",
      description:
        "Ethical sourcing and transparent pricing. We believe in building trust through honest and transparent business practices.",
      color: "text-primary-700",
      bgColor: "bg-primary-50",
    },
    {
      icon: Clock,
      title: "Reliability",
      description:
        "On-time deliveries, every time. Our commitment to punctuality ensures your projects stay on schedule.",
      color: "text-primary-700",
      bgColor: "bg-primary-50",
    },
    {
      icon: Lightbulb,
      title: "Innovation",
      description:
        "Continuous process and product improvements. We leverage technology to enhance your procurement experience.",
      color: "text-primary-700",
      bgColor: "bg-primary-50",
    },
    {
      icon: Leaf,
      title: "Sustainability",
      description:
        "Promoting eco-friendly materials and practices. We are committed to environmental responsibility.",
      color: "text-primary-700",
      bgColor: "bg-primary-50",
    },
    {
      icon: Users,
      title: "Customer-Centricity",
      description:
        "Solutions tailored to your project needs. Your success is our priority in every transaction.",
      color: "text-primary-700",
      bgColor: "bg-primary-50",
    },
  ];

  const team = [
    {
      name: "Nittin Bhinde",
      message:
        "At lohamart India, we believe every great construction project starts with a strong foundation—and that foundation is built with the highest quality materials. Established on integrity, reliability, and a customer-centric approach, we're committed to sourcing only the best products in the market.",
    },
    {
      name: "Dhawal Joshi",
      message:
        "Our strength lies not just in the products we supply, but in the relationships we build. By working closely with clients to understand their unique challenges, we deliver tailored solutions that drive project success and cost efficiency. We're proud to partner in India's growth story, one structure at a time.",
    },
    {
      name: "Rishabh Jaiswal",
      message:
        "Founded with an entrepreneurial spirit and deep market understanding, lohamart bridges the gap between suppliers and customers by providing seamless access to high-quality materials, competitive pricing, and reliable service. Our strong presence in the infrastructure segment allows us to cater to contractors, builders, and businesses efficiently.At lohamart, we believe in combining traditional business values with modern technology. With our dedicated mobile app and website, we are making procurement easier, faster, and more transparent for our customers—ensuring convenience at every step.",
    },
  ];

  return (
    <div className="min-h-screen bg-white pt-40 pb-12">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary-600 to-secondary-600 text-white py-20 relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='80' height='80' viewBox='0 0 80 80' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M0 0h40v40H0V0zm40 40h40v40H40V40z' fill-opacity='0.1'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            }}
          />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center max-w-4xl mx-auto animate-fade-in">
            <div className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-sm px-6 py-3 rounded-full border border-white/20 mb-8">
              <Building2 className="w-5 h-5" />
              <span className="text-sm font-bold tracking-wide">
                lohamart INDIA PRIVATE LIMITED
              </span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6">ABOUT US</h1>
            <p className="text-xl text-gray-100 leading-relaxed">
              Your Strategic Materials Partner for Steel, Structures, and
              Construction Chemicals
            </p>
          </div>
        </div>
      </section>

      {/* Company Description */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center animate-fade-in">
            <p className="text-xl text-gray-700 leading-relaxed mb-6">
              <strong>lohamart India Private Limited</strong> is a newly founded
              company backed by over 10 years of collective experience in
              industrial and construction materials.
            </p>
            <p className="text-lg text-gray-600 leading-relaxed">
              Based in Gujarat, we leverage our decade-long industry know-how, a
              pan-India logistics network, and strong vendor relationships to be
              more than a supplier—we're your strategic materials partner.
            </p>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12">
            {/* Mission Card - Diamond Shape */}
            <div className="animate-slide-up">
              <Card className="bg-gradient-to-br from-secondary-50 to-secondary-100 border-2 border-secondary-200 relative overflow-hidden h-full">
                <div className="flex flex-col items-center text-center p-8">
                  <div className="w-20 h-20 bg-gradient-to-br from-secondary-500 to-secondary-600 rounded-xl flex items-center justify-center flex-shrink-0 mb-6 transform rotate-45">
                    <Target className="w-10 h-10 text-white -rotate-45" />
                  </div>
                  <h3 className="text-3xl font-bold text-gray-900 mb-6">
                    Mission
                  </h3>
                  <p className="text-lg text-gray-700 leading-relaxed">
                    To empower Indian industry with premium materials, delivered
                    on time and backed by expert service.
                  </p>
                </div>
              </Card>
            </div>

            {/* Vision Card - Diamond Shape */}
            <div
              className="animate-slide-up"
              style={{ animationDelay: "100ms" }}
            >
              <Card className="bg-gradient-to-br from-secondary-50 to-secondary-100 border-2 border-secondary-200 relative overflow-hidden h-full">
                <div className="flex flex-col items-center text-center p-8">
                  <div className="w-20 h-20 bg-gradient-to-br from-secondary-500 to-secondary-600 rounded-xl flex items-center justify-center flex-shrink-0 mb-6 transform rotate-45">
                    <Eye className="w-10 h-10 text-white -rotate-45" />
                  </div>
                  <h3 className="text-3xl font-bold text-gray-900 mb-6">
                    Vision
                  </h3>
                  <p className="text-lg text-gray-700 leading-relaxed">
                    To be the country's most trusted, one-stop procurement
                    partner for construction and industrial supplies.
                  </p>
                </div>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16 animate-fade-in">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4 tracking-tight">
              CORE VALUES
            </h2>
            <p className="text-lg text-gray-600">
              The principles that guide everything we do
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-6">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <Card
                  key={value.title}
                  hover
                  className="text-center animate-slide-up border-2 border-gray-100 hover:border-secondary-400"
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <div
                    className={`w-16 h-16 ${value.bgColor} rounded-xl flex items-center justify-center mx-auto mb-4`}
                  >
                    <Icon className={`w-8 h-8 ${value.color}`} />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-3 tracking-wide">
                    {value.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed text-sm">
                    {value.description}
                  </p>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Team Messages */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 animate-fade-in">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-300 to-orange-400">
                MESSAGE FROM
              </span>
              <span className="block text-gray-900 mt-2">OUR PARTNER</span>
            </h2>
          </div>

          <div className="space-y-8 max-w-5xl mx-auto">
            {team.map((member, index) => (
              <Card
                key={member.name}
                className="animate-slide-up bg-white border-l-4 border-secondary-500"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <div className="w-16 h-16 bg-gradient-to-br from-secondary-500 to-secondary-600 rounded-lg flex items-center justify-center">
                      <MessageCircle className="w-8 h-8 text-white" />
                    </div>
                  </div>
                  <div className="flex-1">
                    <div className="inline-block bg-gradient-to-r from-secondary-500 to-secondary-600 px-6 py-2 rounded-lg mb-4">
                      <h3 className="text-xl font-bold text-white">
                        {member.name}
                      </h3>
                    </div>
                    <p className="text-gray-700 leading-relaxed text-lg italic">
                      "{member.message}"
                    </p>
                  </div>
                </div>
              </Card>
            ))}

            {/* Team Signature */}
            <Card className="animate-slide-up bg-gradient-to-br from-primary-600 to-secondary-600 text-white text-center border-0">
              <div className="py-8">
                <MessageCircle className="w-12 h-12 mx-auto mb-4 text-yellow-400" />
                <h3 className="text-2xl font-bold mb-2">Team lohamart</h3>
                <p className="text-gray-300">
                  Sustainability and innovation guide our approach to materials
                  sourcing.
                </p>
              </div>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
